import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Save, X, Plus } from 'lucide-react';
import { MaintenanceTemplate, EditForm, DirectionalInterval } from './types';
import { BuildComponentType, CardinalDirection, MaintenanceIntervalType } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';

interface TemplateEditModalProps {
    show: boolean;
    template: MaintenanceTemplate | null;
    componentType: BuildComponentType;
    isNewTemplate: boolean;
    onSave: (template: EditForm) => Promise<void>;
    onCancel: () => void;
    saving: boolean;
}

// Helper function to translate interval types to Swedish
const translateIntervalType = (intervalType: MaintenanceIntervalType): string => {
    switch (intervalType) {
        case MaintenanceIntervalType.days:
            return 'dagar';
        case MaintenanceIntervalType.weeks:
            return 'veckor';
        case MaintenanceIntervalType.months:
            return 'månader';
        case MaintenanceIntervalType.years:
            return 'år';
        default:
            return 'år';
    }
};

const TemplateEditModal: React.FC<TemplateEditModalProps> = ({
    show,
    template,
    componentType,
    isNewTemplate,
    onSave,
    onCancel,
    saving
}) => {
    const [editForm, setEditForm] = useState<EditForm>({
        name: '',
        description: '',
        interval_type: MaintenanceIntervalType.years,
        interval_value: 1,
        cost: 0,
        cost_unit: 'per_sqm',
        directionalIntervals: []
    });

    useEffect(() => {
        if (show) {
            if (template) {
                setEditForm({
                    name: template.name || '',
                    description: template.description || '',
                    interval_type: template.interval_type || MaintenanceIntervalType.years,
                    interval_value: template.interval_value || 1,
                    cost: template.cost || 0,
                    cost_unit: template.cost_unit || 'per_sqm',
                    directionalIntervals: template.directionalIntervals || []
                });
            } else {
                // Reset for new template
                setEditForm({
                    name: '',
                    description: '',
                    interval_type: MaintenanceIntervalType.years,
                    interval_value: 1,
                    cost: 0,
                    cost_unit: 'per_sqm',
                    directionalIntervals: []
                });
            }
        }
    }, [template, show]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSave(editForm);
    };

    const intervalTypeOptions = [
        { value: MaintenanceIntervalType.days, label: 'Dagar' },
        { value: MaintenanceIntervalType.weeks, label: 'Veckor' },
        { value: MaintenanceIntervalType.months, label: 'Månader' },
        { value: MaintenanceIntervalType.years, label: 'År' }
    ];

    const addDirectionalInterval = (): void => {
        const usedDirections = editForm.directionalIntervals.map(di => di.direction);
        const availableDirections = Object.values(CardinalDirection).filter(
            direction => !usedDirections.includes(direction)
        );

        if (availableDirections.length > 0) {
            setEditForm(prev => ({
                ...prev,
                directionalIntervals: [
                    ...prev.directionalIntervals,
                    {
                        direction: availableDirections[0],
                        interval_value: prev.interval_value,
                        interval_type: prev.interval_type
                    }
                ]
            }));
        }
    };

    const updateDirectionalInterval = (index: number, field: keyof DirectionalInterval, value: any): void => {
        setEditForm(prev => ({
            ...prev,
            directionalIntervals: prev.directionalIntervals.map((di, i) =>
                i === index ? { ...di, [field]: value } : di
            )
        }));
    };

    const removeDirectionalInterval = (index: number): void => {
        setEditForm(prev => ({
            ...prev,
            directionalIntervals: prev.directionalIntervals.filter((_, i) => i !== index)
        }));
    };

    return (
        <Modal show={show} onHide={onCancel} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {isNewTemplate ? 'Skapa ny mall' : 'Redigera mall'} - {translate(componentType)}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    {/* Basic Template Fields */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-6">
                            <label className="form-label fw-medium">Namn</label>
                            <input
                                type="text"
                                className="form-control"
                                value={editForm.name}
                                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="T.ex. Fönstermålning"
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-medium">Beskrivning</label>
                            <input
                                type="text"
                                className="form-control"
                                value={editForm.description}
                                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Valfri beskrivning"
                            />
                        </div>
                    </div>

                    {/* Interval Fields */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Intervall</label>
                            <input
                                type="number"
                                min="1"
                                className="form-control"
                                value={editForm.interval_value}
                                onChange={(e) => setEditForm(prev => ({ ...prev, interval_value: parseInt(e.target.value, 10) }))}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Enhet</label>
                            <select
                                className="form-select"
                                value={editForm.interval_type}
                                onChange={(e) => setEditForm(prev => ({ ...prev, interval_type: e.target.value as MaintenanceIntervalType }))}
                            >
                                {intervalTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Kostnadsenhet</label>
                            <select
                                className="form-select"
                                value={editForm.cost_unit}
                                onChange={(e) => setEditForm(prev => ({ ...prev, cost_unit: e.target.value as 'per_sqm' | 'per_unit' }))}
                            >
                                <option value="per_sqm">kr/kvm</option>
                                <option value="per_unit">kr/st</option>
                            </select>
                        </div>
                    </div>

                    <div className="row g-3 mb-4">
                        <div className="col-md-12">
                            <label className="form-label fw-medium">Kostnad ({editForm.cost_unit === 'per_sqm' ? 'kr/kvm' : 'kr/st'})</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={editForm.cost}
                                onChange={(e) => setEditForm(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                                required
                            />
                        </div>
                    </div>

                    {/* Directional Intervals */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-semibold" style={{color: '#495057'}}>Väderstrecksberoende intervall (valfritt)</h6>
                            {Object.values(CardinalDirection).filter(dir =>
                                !editForm.directionalIntervals.some(di => di.direction === dir)
                            ).length > 0 && (
                                <button
                                    onClick={addDirectionalInterval}
                                    className="btn btn-outline-secondary btn-sm"
                                    type="button"
                                >
                                    <Plus size={14} className="me-1" />
                                    Lägg till väderstreck
                                </button>
                            )}
                        </div>

                        {editForm.directionalIntervals.length === 0 && (
                            <div className="text-muted fst-italic small mb-3">
                                Använder standard intervall ({editForm.interval_value} {translateIntervalType(editForm.interval_type)}) för alla väderstreck
                            </div>
                        )}

                        {editForm.directionalIntervals.map((interval, index) => {
                            const usedDirections = editForm.directionalIntervals.map(di => di.direction);
                            const availableDirections = Object.values(CardinalDirection).filter(
                                direction => !usedDirections.includes(direction) || direction === interval.direction
                            );

                            return (
                                <div key={index} className="card mb-2" style={{background: '#fafbfc', border: '1px solid #e9ecef'}}>
                                    <div className="card-body p-3">
                                        <div className="row g-2 align-items-center">
                                            <div className="col-md-3">
                                                <label className="form-label small fw-medium mb-1">Väderstreck</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={interval.direction}
                                                    onChange={(e) => updateDirectionalInterval(index, 'direction', e.target.value as CardinalDirection)}
                                                >
                                                    {availableDirections.map(direction => (
                                                        <option key={direction} value={direction}>
                                                            {translate(direction)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label small fw-medium mb-1">Intervall</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="form-control form-control-sm"
                                                    value={interval.interval_value}
                                                    onChange={(e) => updateDirectionalInterval(index, 'interval_value', parseInt(e.target.value) || 1)}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label small fw-medium mb-1">Enhet</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={interval.interval_type}
                                                    onChange={(e) => updateDirectionalInterval(index, 'interval_type', e.target.value as MaintenanceIntervalType)}
                                                >
                                                    {intervalTypeOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-2 d-flex align-items-end">
                                                <button
                                                    onClick={() => removeDirectionalInterval(index)}
                                                    className="btn btn-outline-danger btn-sm w-100"
                                                    type="button"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {editForm.directionalIntervals.length > 0 && (
                            <div className="alert alert-info small mt-2 mb-0">
                                <strong>Info:</strong> Väderstreck utan specificerat intervall använder standard intervallet ({editForm.interval_value} {translateIntervalType(editForm.interval_type)}).
                            </div>
                        )}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onCancel}
                    disabled={saving}
                >
                    <X size={16} className="me-1" />
                    Avbryt
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sparar...
                        </>
                    ) : (
                        <>
                            <Save size={16} className="me-1" />
                            Spara
                        </>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default TemplateEditModal;

